import { useFormikContext } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import useDeleteWorkQueueFilterMutation from '../hooks/useDeleteWorkQueueFilterMutation';
import AccordionWrapper from 'App/components/AccordionWrapper/AccordionWrapper';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import useIsPermitted from 'App/utils/hooks/useIsPermitted';
import { PERMISSION_TYPES } from 'App/utils/isPermitted';
import { useUserPreferences } from 'App/contexts/UserPreferencesContext';
import SaveFilterModal from './SaveFilterModal';
import DeleteModal from 'App/components/DeleteModal';
import * as R from 'ramda';
import MyFilterItem from './MyFilterItem';
import HeaderContent from './HeaderContent';

const defaultFilterLength = 5;

const MyFilters = () => {
    const [showAllFilters, setShowAllFilters] = useState(false);
    const {
        values: {
            filterType,
            result: { savedFilterResult },
        },
    } = useFormikContext();
    const filterKey = `${filterType}_savedFilters`;
    const filterOrder = `${filterType}_sortOrder`;
    const deleteWorkQueueFilterMutation = useDeleteWorkQueueFilterMutation();
    const isWorkQueueFiltersEnabled = useIsPermitted(PERMISSION_TYPES.ENABLE_WORK_QUEUE_FILTERS);
    const isWorkQueueIsNotFilterEnabled = useIsPermitted(PERMISSION_TYPES.ENABLE_WORK_QUEUE_IS_NOT);
    const { getPreference, setPreference } = useUserPreferences();
    const [showSaveFilterModal, setShowSaveFilterModal] = useState(false);
    const [showDeleteFilterModal, setShowDeleteFilterModal] = useState(false);
    const [deleteFilter, setDeleteFilter] = useState();
    const [toggleSort, setToggleSort] = useState(() => {
        const storedSortOrder = getPreference(filterOrder);
        if (storedSortOrder && isWorkQueueFiltersEnabled) return storedSortOrder;
        return 'none';
    });
    const [savedFilters, setSavedFilters] = useState(() => {
        const storedFilters = getPreference(filterKey, true);
        const parsedStoredFilters = Array.isArray(storedFilters) ? storedFilters : [];
        const storedFiltersIsNot = isWorkQueueIsNotFilterEnabled
            ? parsedStoredFilters
            : R.map(
                  R.evolve({
                      filter: R.omit(['pendingFilterParams']),
                  }),
                  R.defaultTo([], parsedStoredFilters)
              );
        return storedFilters && isWorkQueueFiltersEnabled ? storedFiltersIsNot : savedFilterResult;
    });

    useEffect(() => {
        if (
            (!R.isEmpty(savedFilterResult) && savedFilters.length !== savedFilterResult.length) ||
            (R.isEmpty(savedFilterResult) && deleteFilter?.uuid)
        ) {
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setSavedFilters((prev) => {
                const newFilters = savedFilterResult.filter((filter) => !prev.some((p) => p.id === filter.id));
                const deletedFilters = prev.filter((filter) => !savedFilterResult.some((p) => p.id === filter.id));
                if (toggleSort !== 'none') {
                    setPreference(
                        filterKey,
                        R.sort(toggleSort === 'asc' && R.ascend(R.prop('name')), savedFilterResult)
                    );
                    return R.sort(toggleSort === 'asc' && R.ascend(R.prop('name')), savedFilterResult);
                }
                setPreference(filterKey, [...prev.filter((filter) => !deletedFilters.includes(filter)), ...newFilters]);
                return [...prev.filter((filter) => !deletedFilters.includes(filter)), ...newFilters];
            });
        }
    }, [savedFilterResult, savedFilters.length, toggleSort, deleteFilter?.uuid, filterKey, setPreference]);

    const filterList = useMemo(() => {
        return showAllFilters ? savedFilters : savedFilters.slice(0, defaultFilterLength);
    }, [savedFilters, showAllFilters]);

    const handleSort = () => {
        setToggleSort('asc');
        setSavedFilters((prev) => R.sort(R.ascend(R.prop('name')), prev));
        setPreference(filterKey, R.sort(R.ascend(R.prop('name')), savedFilters));
        setPreference(filterOrder, 'asc');
    };

    const showMoreText =
        savedFilterResult.length > defaultFilterLength
            ? `${showAllFilters ? 'Hide' : 'Show'} ${savedFilterResult.length - defaultFilterLength} more...`
            : null;

    const onDragEnd = (result) => {
        if (!result.destination) {
            return;
        }
        const { source, destination } = result;
        const newItems = [...savedFilters];
        const [movedItem] = newItems.splice(source.index, 1);
        newItems.splice(destination.index, 0, movedItem);
        setSavedFilters(newItems);
        setPreference(filterKey, newItems);
        setToggleSort('none');
        setPreference(filterOrder, 'none');
    };

    if (!isWorkQueueFiltersEnabled && filterList.length < 1) {
        return null;
    }

    const innerContentWithFiltersFlag = (
        <div className="filter-section">
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="filter-list">
                    {(provided) => (
                        <div
                            className="filter form-group"
                            data-testid="filter-list"
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                        >
                            {filterList.map((filter, index) => {
                                return (
                                    <MyFilterItem
                                        key={filter.id}
                                        filter={filter}
                                        filterIndex={index}
                                        setShowDeleteFilterModal={setShowDeleteFilterModal}
                                        setDeleteFilter={setDeleteFilter}
                                    />
                                );
                            })}

                            {provided.placeholder}
                            {savedFilterResult.length > defaultFilterLength && (
                                <Button
                                    aria-label={showMoreText}
                                    variant="link"
                                    className="show-more-text"
                                    onClick={() => setShowAllFilters(!showAllFilters)}
                                >
                                    {showMoreText}
                                </Button>
                            )}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </div>
    );

    const innerContent = (
        <div className="filter-section">
            <span
                aria-label="My Filters"
                className="filter-section-header filter-section-header-saved-filters control-label"
            >
                My Filters
            </span>
            <div className="filter form-group" data-testid="filter-list">
                {filterList.map((filter) => (
                    <MyFilterItem
                        key={filter.id}
                        filter={filter}
                        setShowDeleteFilterModal={setShowDeleteFilterModal}
                        setDeleteFilter={setDeleteFilter}
                    />
                ))}
                {savedFilterResult.length > defaultFilterLength && (
                    <Button
                        aria-label={showMoreText}
                        variant="link"
                        className="filter-record"
                        onClick={() => setShowAllFilters(!showAllFilters)}
                    >
                        {showMoreText}
                    </Button>
                )}
            </div>
        </div>
    );

    return (
        <div>
            {isWorkQueueFiltersEnabled ? (
                <AccordionWrapper
                    title="My Saved Filters"
                    stateKey="workQueueSavedFilters-toggle"
                    HeaderContent={HeaderContent}
                >
                    <>
                        {filterList.length ? <> {innerContentWithFiltersFlag} </> : null}
                        {filterList.length > 1 ? (
                            <Button
                                aria-label="Sort Alphabetically"
                                variant="link"
                                className="sort-btn"
                                onClick={() => handleSort()}
                            >
                                Sort Alphabetically
                            </Button>
                        ) : null}

                        <Button
                            aria-label="Save Filter"
                            variant="primary"
                            className="save-filter"
                            onClick={() => setShowSaveFilterModal(!showSaveFilterModal)}
                        >
                            Save Filter
                        </Button>

                        {showSaveFilterModal && (
                            <SaveFilterModal
                                onHide={() => {
                                    setShowSaveFilterModal(false);
                                }}
                            />
                        )}

                        {showDeleteFilterModal && (
                            <DeleteModal
                                onHide={() => {
                                    setShowDeleteFilterModal(false);
                                }}
                                show
                                title="Delete Filter"
                                message="Are you sure you want to delete this filter?"
                                onDelete={() => {
                                    return deleteWorkQueueFilterMutation.mutateAsync(deleteFilter).then(() => {
                                        setShowDeleteFilterModal(false);
                                    });
                                }}
                            />
                        )}
                    </>
                </AccordionWrapper>
            ) : (
                innerContent
            )}
        </div>
    );
};

export default MyFilters;
